// KPI Drill-down Configuration based on Power BI-style interactive experience

import { KpiDrillDownConfig, DrillDownLevelConfig, ExtraInteraction, KpiType, DrillDownLevel } from '../types/drilldown';

// Helper function to create level configurations
const createLevelConfig = (
  level: DrillDownLevel,
  label: string,
  description: string,
  dimensions: string[],
  aggregationType: 'sum' | 'average' | 'count' | 'percentage' = 'count',
  requiresPermission?: string[]
): DrillDownLevelConfig => ({
  level,
  label,
  description,
  dimensions,
  aggregationType,
  requiresPermission,
  sortBy: dimensions[0],
  defaultSort: 'desc'
});

// 1. Total Employees (Active Workforce) Configuration
const totalEmployeesConfig: KpiDrillDownConfig = {
  kpi: 'totalEmployees',
  levels: [
    createLevelConfig(
      'overview',
      'Total Employees',
      'Total headcount across all selected filters',
      ['total']
    ),
    createLevelConfig(
      'department',
      'By Department/Function', 
      'Headcount split by departments (Engineering, HR, Sales)',
      ['department', 'function']
    ),
    createLevelConfig(
      'geography',
      'By Country/Region',
      'Department totals broken into country-level numbers',
      ['country', 'region']
    ),
    createLevelConfig(
      'demographic',
      'By Job Level/Grade',
      'Employee count per grade (L1–L10)',
      ['jobLevel', 'grade', 'employmentType']
    ),
    createLevelConfig(
      'individual',
      'Individual Records',
      'List of employees with key details',
      ['employeeId', 'name', 'department', 'country', 'jobLevel'],
      'count',
      ['viewIndividualData']
    )
  ],
  extraInteractions: [
    {
      type: 'trend',
      label: 'View Trend Over Time',
      action: 'showHeadcountTrend',
      targetView: 'trend-monthly'
    },
    {
      type: 'comparison',
      label: 'Compare to Last Year',
      action: 'showYearOverYear'
    }
  ],
  rlsRequired: true
};

// 2. Attrition Risk Configuration  
const attritionRiskConfig: KpiDrillDownConfig = {
  kpi: 'attritionRisk',
  levels: [
    createLevelConfig(
      'overview',
      'Attrition Risk',
      'Percentage of workforce flagged as high/medium attrition risk',
      ['riskLevel'],
      'percentage'
    ),
    createLevelConfig(
      'demographic',
      'By Risk Category',
      'High, Medium, Low risk distribution',
      ['riskCategory', 'riskScore']
    ),
    createLevelConfig(
      'department',
      'By Department/Function',
      'Attrition risk percentage per department',
      ['department', 'function'],
      'percentage'
    ),
    createLevelConfig(
      'geography',
      'By Country/Region',
      'Attrition risk percentage per country',
      ['country', 'region'],
      'percentage'
    ),
    createLevelConfig(
      'individual',
      'Employee Risk List',
      'Names, tenure, risk factors, last engagement score',
      ['employeeId', 'name', 'tenure', 'riskFactors', 'engagementScore'],
      'count',
      ['viewIndividualData', 'viewSensitiveData']
    )
  ],
  extraInteractions: [
    {
      type: 'breakdown',
      label: 'View Attrition Drivers',
      action: 'showRiskDrivers',
      targetView: 'attrition-drivers'
    },
    {
      type: 'link',
      label: 'Open Retention Planning',
      action: 'openRetentionPlan',
      requiresPermission: ['manageRetention']
    }
  ],
  rlsRequired: true
};

// 3. Engagement Score Configuration
const engagementScoreConfig: KpiDrillDownConfig = {
  kpi: 'engagementScore',
  levels: [
    createLevelConfig(
      'overview',
      'Engagement Score',
      'Average engagement score (out of 10) for the workforce',
      ['avgScore'],
      'average'
    ),
    createLevelConfig(
      'department',
      'By Department/Function',
      'Average engagement score per department',
      ['department', 'function'],
      'average'
    ),
    createLevelConfig(
      'geography',
      'By Country/Region',
      'Engagement score by geography',
      ['country', 'region'],
      'average'
    ),
    createLevelConfig(
      'demographic',
      'By Tenure Bands',
      'Engagement by employee tenure group',
      ['tenureBand', 'ageGroup', 'jobLevel']
    ),
    createLevelConfig(
      'individual',
      'Survey Question Breakdown',
      'Average scores for individual survey questions',
      ['questionCategory', 'question', 'score']
    )
  ],
  extraInteractions: [
    {
      type: 'comparison',
      label: 'Overlay with Attrition Risk',
      action: 'overlayAttritionRisk',
      targetView: 'engagement-vs-attrition'
    },
    {
      type: 'trend',
      label: 'Engagement Trends',
      action: 'showEngagementTrends'
    }
  ],
  rlsRequired: false
};

// 4. Leadership Ready (Succession Pipeline) Configuration
const leadershipReadyConfig: KpiDrillDownConfig = {
  kpi: 'leadershipReady',
  levels: [
    createLevelConfig(
      'overview',
      'Leadership Ready',
      'Percentage of critical roles with at least one ready successor',
      ['readinessPercentage'],
      'percentage'
    ),
    createLevelConfig(
      'demographic',
      'By Role Criticality',
      'Split into High, Medium, Low criticality roles',
      ['criticality', 'roleType']
    ),
    createLevelConfig(
      'department',
      'By Department/Function',
      'Readiness by department',
      ['department', 'function'],
      'percentage'
    ),
    createLevelConfig(
      'geography',
      'By Country/Region',
      'Readiness per country',
      ['country', 'region'],
      'percentage'
    ),
    createLevelConfig(
      'individual',
      'Role/Incumbent List',
      'Role, incumbent, identified successors, readiness rating',
      ['role', 'incumbent', 'successors', 'readinessRating'],
      'count',
      ['viewIndividualData', 'viewSuccessionData']
    )
  ],
  extraInteractions: [
    {
      type: 'link',
      label: 'Succession Planning Matrix',
      action: 'openSuccessionMatrix',
      targetView: 'succession-matrix',
      requiresPermission: ['viewSuccessionPlanning']
    },
    {
      type: 'breakdown',
      label: 'Development Gap Analysis',
      action: 'showGapAnalysis'
    }
  ],
  rlsRequired: true
};

// 5. Time to Fill (Average Duration) Configuration
const timeToFillConfig: KpiDrillDownConfig = {
  kpi: 'timeToFill',
  levels: [
    createLevelConfig(
      'overview',
      'Time to Fill',
      'Average number of days to fill a position',
      ['avgDays'],
      'average'
    ),
    createLevelConfig(
      'demographic',
      'By Role Type',
      'Time to fill by role category (technical, operational, leadership)',
      ['roleType', 'roleCategory', 'seniorityLevel']
    ),
    createLevelConfig(
      'department',
      'By Department/Function',
      'Average time per department',
      ['department', 'function'],
      'average'
    ),
    createLevelConfig(
      'geography',
      'By Country/Region',
      'Geographic variation in hiring time',
      ['country', 'region', 'location'],
      'average'
    ),
    createLevelConfig(
      'individual',
      'Requisition List',
      'Position, posted date, filled date, recruiter',
      ['positionId', 'jobTitle', 'postedDate', 'filledDate', 'recruiter', 'daysToFill'],
      'count',
      ['viewRecruitmentData']
    )
  ],
  extraInteractions: [
    {
      type: 'trend',
      label: 'Historical Trends (12-24 months)',
      action: 'showHistoricalTrends',
      targetView: 'time-to-fill-trends'
    },
    {
      type: 'comparison',
      label: 'Benchmark vs Industry',
      action: 'showBenchmarks'
    }
  ],
  rlsRequired: false
};

// 6. Diversity Score (DEI Metrics) Configuration
const diversityScoreConfig: KpiDrillDownConfig = {
  kpi: 'diversityScore',
  levels: [
    createLevelConfig(
      'overview',
      'Diversity Score',
      'Percentage of workforce meeting diversity targets',
      ['diversityPercentage'],
      'percentage'
    ),
    createLevelConfig(
      'demographic',
      'By DEI Category',
      'Gender, Ethnicity, Age Group, Disability, Other',
      ['gender', 'ethnicity', 'ageGroup', 'disability', 'other']
    ),
    createLevelConfig(
      'department',
      'By Department/Function',
      'DEI metrics by department',
      ['department', 'function'],
      'percentage'
    ),
    createLevelConfig(
      'geography',
      'By Country/Region',
      'Geographic DEI performance',
      ['country', 'region'],
      'percentage'
    ),
    createLevelConfig(
      'individual',
      'Individual Records',
      'Employee anonymized ID and diversity attributes',
      ['anonymizedId', 'diversityAttributes'],
      'count',
      ['viewIndividualData', 'viewDiversityData']
    )
  ],
  extraInteractions: [
    {
      type: 'comparison',
      label: 'Trend vs DEI Targets',
      action: 'showTargetProgress',
      targetView: 'dei-target-progress'
    },
    {
      type: 'breakdown',
      label: 'Representation Analysis',
      action: 'showRepresentationAnalysis'
    }
  ],
  rlsRequired: true
};

// Master configuration export
export const DRILL_DOWN_CONFIGS: Record<KpiType, KpiDrillDownConfig> = {
  totalEmployees: totalEmployeesConfig,
  attritionRisk: attritionRiskConfig,
  engagementScore: engagementScoreConfig,
  leadershipReady: leadershipReadyConfig,
  timeToFill: timeToFillConfig,
  diversityScore: diversityScoreConfig
};

// Helper functions for configuration access
export const getDrillDownConfig = (kpi: KpiType): KpiDrillDownConfig => {
  return DRILL_DOWN_CONFIGS[kpi];
};

export const getAvailableLevels = (kpi: KpiType): DrillDownLevelConfig[] => {
  return DRILL_DOWN_CONFIGS[kpi].levels;
};

export const getExtraInteractions = (kpi: KpiType): ExtraInteraction[] => {
  return DRILL_DOWN_CONFIGS[kpi].extraInteractions;
};

export const requiresRLS = (kpi: KpiType): boolean => {
  return DRILL_DOWN_CONFIGS[kpi].rlsRequired;
};

// Navigation helpers
export const getNextLevel = (kpi: KpiType, currentLevel: DrillDownLevel): DrillDownLevel | null => {
  const config = DRILL_DOWN_CONFIGS[kpi];
  const currentIndex = config.levels.findIndex(l => l.level === currentLevel);
  
  if (currentIndex === -1 || currentIndex === config.levels.length - 1) {
    return null;
  }
  
  return config.levels[currentIndex + 1].level;
};

export const getPreviousLevel = (kpi: KpiType, currentLevel: DrillDownLevel): DrillDownLevel | null => {
  const config = DRILL_DOWN_CONFIGS[kpi];
  const currentIndex = config.levels.findIndex(l => l.level === currentLevel);
  
  if (currentIndex <= 0) {
    return null;
  }
  
  return config.levels[currentIndex - 1].level;
};

export const canDrillDown = (kpi: KpiType, currentLevel: DrillDownLevel, userPermissions: string[]): boolean => {
  const nextLevel = getNextLevel(kpi, currentLevel);
  if (!nextLevel) return false;
  
  const config = DRILL_DOWN_CONFIGS[kpi];
  const nextLevelConfig = config.levels.find(l => l.level === nextLevel);
  
  if (!nextLevelConfig?.requiresPermission) return true;
  
  return nextLevelConfig.requiresPermission.every(permission => 
    userPermissions.includes(permission)
  );
};